import { useState, useRef } from "react";
import { Upload, Loader2, Check, X } from "lucide-react";
import api from "../lib/api";
import { toast } from "sonner";

/**
 * FileUpload — uploads to backend object storage, returns the served URL.
 * Props:
 *   kind: "video" | "image" — affects accept attribute
 *   currentUrl: pre-existing URL (display preview)
 *   onUploaded: (url) => void
 *   testId: data-testid prefix
 */
export default function FileUpload({
    kind = "image",
    currentUrl,
    onUploaded,
    testId = "file-upload",
    label,
}) {
    const [uploading, setUploading] = useState(false);
    const [progress, setProgress] = useState(0);
    const inputRef = useRef(null);
    const accept = kind === "video" ? "video/*" : "image/*";

    const handleFile = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        const max = kind === "video" ? 200 : 10; // MB
        if (file.size > max * 1024 * 1024) {
            toast.error(`Fichier trop volumineux (max ${max} Mo)`);
            return;
        }
        setUploading(true);
        setProgress(0);
        try {
            const fd = new FormData();
            fd.append("file", file);
            fd.append("kind", kind);
            const { data } = await api.post("/upload", fd, {
                headers: { "Content-Type": "multipart/form-data" },
                onUploadProgress: (p) => {
                    if (p.total) {
                        setProgress(Math.round((p.loaded / p.total) * 100));
                    }
                },
            });
            // Iter-27.88 — On stocke une URL RELATIVE (/api/files/...) au lieu de
            // l'URL absolue avec REACT_APP_BACKEND_URL. Sinon, les images uploadées
            // sur la preview restent gravées avec le domaine preview et cassent
            // une fois servies depuis la prod (academy.shariti.com).
            const relativeUrl = `/api/files/${data.storage_path}`;
            onUploaded?.(relativeUrl);
            toast.success("Upload terminé");
        } catch (err) {
            toast.error("Erreur d'upload");
        } finally {
            setUploading(false);
            setProgress(0);
            if (inputRef.current) inputRef.current.value = "";
        }
    };

    return (
        <div className="space-y-2">
            {label && (
                <label className="text-xs font-thin tracking-[0.2em] uppercase text-white/50">
                    {label}
                </label>
            )}
            <div className="flex items-center gap-3">
                <input
                    ref={inputRef}
                    type="file"
                    accept={accept}
                    onChange={handleFile}
                    className="hidden"
                    data-testid={`${testId}-input`}
                />
                <button
                    type="button"
                    onClick={() => inputRef.current?.click()}
                    disabled={uploading}
                    data-testid={`${testId}-button`}
                    className="flex items-center gap-2 px-4 py-2.5 rounded-2xl glass-card hover:border-shariti-orange/40 text-sm font-bold text-white transition-all disabled:opacity-60"
                >
                    {uploading ? (
                        <>
                            <Loader2 className="w-4 h-4 animate-spin" />
                            {progress}%
                        </>
                    ) : currentUrl ? (
                        <>
                            <Check className="w-4 h-4 text-shariti-orange" />
                            Remplacer le {kind === "video" ? "vidéo" : "fichier"}
                        </>
                    ) : (
                        <>
                            <Upload className="w-4 h-4" />
                            Uploader {kind === "video" ? "une vidéo" : "une image"}
                        </>
                    )}
                </button>
                {currentUrl && !uploading && (
                    <button
                        type="button"
                        onClick={() => onUploaded?.("")}
                        data-testid={`${testId}-clear`}
                        className="p-2 rounded-xl hover:bg-white/5 text-white/50"
                        title="Retirer"
                    >
                        <X className="w-4 h-4" />
                    </button>
                )}
            </div>
            {currentUrl && kind === "image" && (
                <img
                    src={currentUrl}
                    alt="preview"
                    className="w-full h-32 object-cover rounded-2xl border border-white/10"
                />
            )}
            {currentUrl && kind === "video" && (
                <p className="text-xs text-white/40 truncate" data-testid={`${testId}-current`}>
                    {currentUrl}
                </p>
            )}
        </div>
    );
}
